import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Journey, JourneyContext } from './journey';
import { notifyClose, notifyIneligible, notifySuccess } from './parent';
import { initialUi, ScreenName, screenFor, stepOf, stepsFor, UiState } from './screens';
import Assessing from './screens/Assessing';
import BankStatement from './screens/BankStatement';
import LoanOffer from './screens/LoanOffer';
import { AllSet, ChooseCheaperProduct, Closed, NotEligible, SessionProblem, SettingUpLoan, Starting } from './screens/Outcomes';
import { ConfirmingPayment, PaymentSuccessful, UpfrontPayment } from './screens/Payment';
import YourInformation from './screens/YourInformation';
import { useSession } from './useSession';

const SCREENS: Record<ScreenName, ComponentType> = {
  yourInformation: YourInformation,
  bankStatement: BankStatement,
  assessing: Assessing,
  loanOffer: LoanOffer,
  upfrontPayment: UpfrontPayment,
  confirmingPayment: ConfirmingPayment,
  paymentSuccessful: PaymentSuccessful,
  settingUpLoan: SettingUpLoan,
  allSet: AllSet,
  notEligible: NotEligible,
  chooseCheaperProduct: ChooseCheaperProduct,
  closed: Closed,
};

type Ui = UiState & { paymentReference?: string };

/**
 * The embedded lending journey. The backend's session decides the screen (screenFor); this keeps only what the
 * SDK alone knows, and tells the merchant's page when the equity is paid, when the customer is not eligible and
 * when the SDK is closed.
 */
const LendingApp = () => {
  const [ui, setUi] = useState<Ui>(initialUi);
  const { session, error, setSession, refresh } = useSession(ui);
  const reported = useRef({ success: false, ineligible: false });

  const updateUi = useCallback((change: Partial<Ui>) => setUi((current) => ({ ...current, ...change })), []);

  const close = useCallback(
    () => notifyClose({ applicationReference: session?.applicationReference, status: session?.status }),
    [session?.applicationReference, session?.status],
  );

  useEffect(() => {
    if (!session) {
      return;
    }
    const result = { applicationReference: session.applicationReference, status: session.status };
    if (session.equityPayment?.status === 'PAID' && !reported.current.success) {
      reported.current.success = true;
      notifySuccess(result);
    }
    if (session.eligibility?.outcome === 'INELIGIBLE' && !reported.current.ineligible) {
      reported.current.ineligible = true;
      notifyIneligible({
        ...result,
        reasonCode: session.eligibility.reasonCode,
        maxPurchaseAmount: session.eligibility.maxPurchaseAmount,
        maxFinancedAmount: session.eligibility.maxFinancedAmount,
      });
    }
    // A declined payment ends the wait: the customer is asked to pay again.
    if (session.equityPayment?.status === 'FAILED' && ui.awaitingPaymentConfirmation) {
      updateUi({ awaitingPaymentConfirmation: false });
    }
  }, [session, ui.awaitingPaymentConfirmation, updateUi]);

  const journey = useMemo<Journey | null>(() => {
    if (!session) {
      return null;
    }
    const steps = stepsFor(session);
    const screen = screenFor(session, ui);
    return {
      steps: steps.map((s) => s.label),
      current: stepOf(steps, screen),
      session,
      ui,
      updateUi,
      setSession,
      refresh,
      close,
    };
  }, [session, ui, updateUi, setSession, refresh, close]);

  if (!journey) {
    return error ? <SessionProblem message={error.message} onClose={close} /> : <Starting onClose={close} />;
  }
  const Screen = SCREENS[screenFor(journey.session, ui)];
  return (
    <JourneyContext.Provider value={journey}>
      <Screen />
    </JourneyContext.Provider>
  );
};

export default LendingApp;
